import { Typography, Stack, TextField, List, ListItemButton, ListItem, ListItemIcon, ListItemText, Checkbox, Grid, DialogContent, DialogTitle, Divider, Box, useTheme } from '@mui/material';
import React, { Fragment, useState } from 'react';

import EditAttributesTwoToneIcon from '@mui/icons-material/EditAttributesTwoTone';


import { IsEmpty } from '../Helpers';
import ManageOrganizationClient, { OrganizationRoleOptions, OrganizationUser, SecuredActivity } from '../apiClients/ManageOrganizationClient';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { OrgAssociationType, Securables } from '../Enums';
import { lightBlue } from '@mui/material/colors';
import { useErrorDisplay } from '../components/ErrorDisplay';
import EmployeeClient, { EmployeeDetail } from '../apiClients/EmployeeClient';
import IconButtonEx, { IconButtonExProps } from '../components/IconButtonEx';
import ButtonEx from '../components/ButtonEx';
import BusySpinner from '../components/BusySpinner';
import TZGravatar from '../components/TZGravatar';
import useViewportMode from '../hooks/useViewportMode';
import { ResponsiveDialog, StickyDialogActions } from '../components/responsive';

export interface EmployeEditorPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    user: OrganizationUser;
    associationType: OrgAssociationType;
    icon?: JSX.Element;
}

const EmployeeEditorPopup = ({ user, associationType, icon = <EditAttributesTwoToneIcon htmlColor='lightblue' fontSize='small' />, ...btnProps }: EmployeEditorPopupProps) => {
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [roleOptions, setRoleOptions] = useState<OrganizationRoleOptions>();
    const [employeeDetail, setEmployeeDetail] = useState<EmployeeDetail>();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const { reloadAccessToken, accessInfo, hasRole } = useAccessInfo();
    const theme = useTheme();
    const { isPhone, isTablet, isDesktop, isLandscape } = useViewportMode();
    const useSplitContent = isDesktop || (isTablet && isLandscape);

    const loadData = async () => {
        try {
            setBusy(true);
            if (IsEmpty(roleOptions)) {
                var pr = await new ManageOrganizationClient().GetPossibleRoles();
                console.log(pr);
                setRoleOptions(pr);
            }
            var ed = await new EmployeeClient().GetEmployeeDetail(user.Id);
            setEmployeeDetail(ed);
        } catch (error) {
            setError(error, "Failed to load items")
        } finally {
            setBusy(false);
        }
    }

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    }

    const handleRoleClicked = async (role: string) => {
        try {
            setBusy(true);
            var sa = roleOptions?.SecuredActivities.find(x => x.Key === role);
            if (user.Id === accessInfo.currentUserId && employeeDetail.Roles.includes(role) && (sa.Key === Securables.organization_employee_edit || sa.Key === Securables.organization_employee)) {
                setError('You can not take away your permissons to perform this action', "Error updating Roles");
                return;
            }

            if (employeeDetail.Roles.includes(role)) {
                let roles = await new EmployeeClient().RemoveRole(user.Id, role);
                setEmployeeDetail({ ...employeeDetail, Roles: roles });
            }
            else {
                let roles = await new EmployeeClient().AddRole(user.Id, role);
                setEmployeeDetail({ ...employeeDetail, Roles: roles });
            }


        } catch (error) {
            setError(error, "Failed to update the users roles");
        } finally {
            setBusy(false);
        }
    }

    const handleClose = async () => {
        setOpen(false);
        if (user.Id === accessInfo.currentUserId) {
            await reloadAccessToken();
        }
    }

    const securedActivitesFilter = (x: SecuredActivity) => {
        return x.RoleTemplates.includes('Assessor') || x.RoleTemplates.includes('Templator') || x.RoleTemplates.includes('User') || x.RoleTemplates.includes('Admin');
        // TODO determine what 'possible' permissions can be assigned to a Consultant ie Assessor
        // switch (associationType) {
        //     case OrgAssociationType.Employee:
        //         return x.RoleTemplates.includes('Assessor') || x.RoleTemplates.includes('Templator') || x.RoleTemplates.includes('User') || x.RoleTemplates.includes('Admin');
        //     case OrgAssociationType.Consultant:
        //         return x.RoleTemplates.includes('Consultant');
        // }
    }

    const roleGroups = roleOptions?.SecuredActivities?.filter(securedActivitesFilter)?.map(x => x.Group).filter((v, i, a) => a.indexOf(v) == i) ?? [];
    const securedActivites = roleOptions?.SecuredActivities?.filter(securedActivitesFilter);


    const securableDisabled = (securableName: string) => {

        if ((employeeDetail?.Roles?.findIndex(x => x === Securables.synergist_admin) >= 0 ?? false))
            return true;
        let test = (user.Id === accessInfo.currentUserId && [Securables.organization_administrator, Securables.organization_employee_edit].includes(securableName as Securables)); //makes sure the user can not remove the access to perform this action from themselves
        test = test || ((employeeDetail?.Roles?.findIndex(x => x.startsWith(securableName)) >= 0 ?? false) && !(employeeDetail?.Roles?.includes(securableName) ?? false))//disables the removal of securables granted by child permissions
        test = test || (!hasRole(Securables.organization_administrator) && securableName !== Securables.organization_administrator);

        return test;
    }

    return (

        <>
            <IconButtonEx disabled={busy} onClick={handleOpen} size='small' hoverContent='Edit User' {...btnProps}>{icon}</IconButtonEx>
            <ErrorDisplay displayType='message' />


            <ResponsiveDialog maxWidth='md' open={open} fullWidth={true} aria-labelledby="responsive-dialog-title">
                <BusySpinner busy={busy}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    <DialogTitle id="responsive-dialog-title" sx={{ backgroundColor: theme.palette.primary.main }}>
                        <Stack direction='row'>
                            <Typography sx={{ marginLeft: theme.spacing(2), flex: 1 }} style={{ width: '100%' }}>{`${employeeDetail?.LastName}, ${employeeDetail?.FirstName}`}</Typography>
                        </Stack>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ px: isPhone ? 2 : 3, py: isPhone ? 2 : 3, overflowY: 'auto' }}>
                        <Grid container spacing={3}>
                            <Grid item xs={useSplitContent ? 6 : 12}>
                                <Stack spacing={2}>
                                    <Stack direction={isPhone ? 'column-reverse' : 'row'} spacing={2} alignItems={isPhone ? 'center' : 'flex-start'}>
                                        <Stack spacing={2} style={{ width: '100%' }}>
                                            <TextField label="First Name" fullWidth
                                                error={IsEmpty(employeeDetail?.FirstName)}
                                                size='small'
                                                variant="outlined"
                                                value={employeeDetail?.FirstName ?? ''} />
                                            <TextField label="Last Name" fullWidth
                                                error={IsEmpty(employeeDetail?.LastName)}
                                                size='small'
                                                variant="outlined"
                                                value={employeeDetail?.LastName ?? ''} />
                                        </Stack>
                                        <TZGravatar email={employeeDetail?.EmailAddressLowered} size={100} type='image' />
                                    </Stack>
                                    <TextField label="Email" fullWidth
                                        error={IsEmpty(employeeDetail?.EmailAddressLowered)}
                                        size='small'
                                        variant="outlined"
                                        value={employeeDetail?.EmailAddressLowered ?? ''} />
                                </Stack>
                            </Grid>
                            <Grid item xs={useSplitContent ? 6 : 12}>
                                <Stack spacing={0}>
                                    <Typography>Roles:</Typography>
                                    <Box border={1} borderColor={theme.palette.divider} padding='5px' sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <List id='rolesList' dense>
                                            {roleGroups.map(rg => (
                                                <Fragment key={rg}>
                                                    <Typography sx={{
                                                        color: lightBlue[700],
                                                        marginTop: theme.spacing(0),
                                                        marginLeft: theme.spacing(0)
                                                    }}>{rg}</Typography>
                                                    {securedActivites.filter(x => x.Group === rg).map(sa => (
                                                        <ListItem disablePadding key={sa.Key} dense>
                                                            <ListItemButton
                                                                onClick={() => handleRoleClicked(sa.Key)}
                                                                dense
                                                                sx={{ minHeight: isDesktop ? undefined : 44 }}
                                                                disabled={securableDisabled(sa.Key) && (employeeDetail?.Roles?.findIndex(x => x.startsWith(sa.Key)) >= 0 ?? false)}>
                                                                <ListItemIcon>
                                                                    <Checkbox style={{ padding: '0px' }}
                                                                        edge="start"
                                                                        checked={(employeeDetail?.Roles?.findIndex(x => x.startsWith(sa.Key)) >= 0 ?? false) || (employeeDetail?.Roles?.findIndex(x => x === Securables.synergist_admin) >= 0 ?? false)}
                                                                        tabIndex={-1}
                                                                        disableRipple
                                                                        inputProps={{ 'aria-labelledby': `${sa.Key}-item` }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText id={`${sa.Key}-item`} primary={sa.Name} />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    ))}
                                                    <div style={{ marginBottom: '15px' }} />
                                                </Fragment>
                                            )
                                            )}
                                        </List>
                                    </Box>
                                    <Divider />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <Divider />
                    <StickyDialogActions phoneLayout='inline'>
                        <ButtonEx onClick={handleClose} variant='text' color='inherit'>Close</ButtonEx>
                    </StickyDialogActions>
                    </Box>

                </BusySpinner>
            </ResponsiveDialog>
        </>
    )
}

export default EmployeeEditorPopup
